import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import { NOTIFICATIONS_QUERY_KEY } from './use-notifications';

/**
 * W22-F7-S2 — execute an invitation action (Accept / Decline) inline.
 *
 * The shape consumed here is the W17-F4 `actionButtons` payload that
 * notification authors attach to invitation-class notifications:
 *
 *   { label: 'Accept', url: '/api/v1/...', method: 'POST', variant: 'primary' }
 *
 * Approach: this hook is **transport-only** — it forwards to whatever URL
 * the notification author specified. That keeps the UI faithful to the
 * W17-F4 contract and avoids re-implementing invitation domain logic
 * (which lives behind W21's `/api/v1/auth/accept-invitation` token flow
 * for email-link acceptance, and would need a Core-side per-id accept
 * surface to support inline actions; see follow-up note in JSDoc).
 *
 * Side effects:
 *   - On success: invalidate ['notifications'], ['workspaces'], ['partners']
 *     so the row disappears and any downstream listings refresh.
 *   - On error: surfaces the error to the caller for a toast.
 *
 * Follow-up: Core does not yet expose a per-invitation `accept` / `decline`
 * mutation. When the W17-F4 author of an `Invitation` notification leaves
 * the actionButton URL blank, the row simply doesn't render the inline
 * buttons and the user falls back to the email-link accept flow (W21-F2).
 */

export interface InvitationAction {
  url: string;
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Optional body to include — used when the W17-F4 payload supplies one. */
  body?: unknown;
}

function useInvitationMutation(): UseMutationResult<void, Error, InvitationAction> {
  const { getAccessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<void, Error, InvitationAction>({
    mutationFn: async (action: InvitationAction) => {
      await apiFetch<unknown>(action.url, {
        method: action.method ?? 'POST',
        getAccessToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action.body ?? {}),
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ['workspaces'] });
      void qc.invalidateQueries({ queryKey: ['partners'] });
    },
  });
}

/** Accept an invitation by following the W17-F4 actionButton URL. */
export function useAcceptInvitation(): UseMutationResult<
  void,
  Error,
  InvitationAction
> {
  return useInvitationMutation();
}

/** Decline an invitation by following the W17-F4 actionButton URL. */
export function useDeclineInvitation(): UseMutationResult<
  void,
  Error,
  InvitationAction
> {
  return useInvitationMutation();
}
